import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import CrmSyncUpsellImageSrc from '../../../../assets/images/crmSyncUpsell.svg';
import {CrmSyncSourcesEnum} from '../../../../models/api/crmSyncModel';
import {UpsellCard} from '../../../common/upsellCard/upsellCard';
import {Pill} from '../../../pill/pill';
import {computeHelpCenterLink} from './crmSyncHelpers';

/*
 * Constants.
 */

const billingUrl = '/settings/billing';
const helperText = 'Ask your admin about upgrading';

/*
 * Props.
 */

interface CrmSyncUpsellProps {
  isAdmin: boolean;
  crmSyncSource: CrmSyncSourcesEnum;
  navigateToLink: HandlerOf<string>;
}

/*
 * Styles.
 */

const UpsellWrapperDiv = styled.div`
  width: 100%;
`;

/*
 * Style.
 */

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  margin-left: 4px;
  margin-bottom: -2px;
`;

/*
 * Component.
 */

export const CrmSyncUpsell: FC<CrmSyncUpsellProps> = (props) => {
  const {navigateToLink, crmSyncSource, isAdmin} = props;

  return (
    <UpsellWrapperDiv>
      <UpsellCard
        title={<Trans id="crm-sync-upsell-title">Instantly Sync Accounts from Salesforce to Front</Trans>}
        pill={
          <Pill size={VisualSizesEnum.MEDIUM}>
            <Trans id="crm-sync-upsell-pill">AVAILABLE WITH GROWTH</Trans>
          </Pill>
        }
        imageUrl={CrmSyncUpsellImageSrc}
        buttons={[
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={() => navigateToLink(billingUrl)}
            isDisabled={!isAdmin}
          >
            <Trans id="crm-sync-upsell-button">Upgrade</Trans>
          </Button>,
          <Button
            variant={ButtonVariantsEnum.LINK}
            onClick={() => openInBrowser(computeHelpCenterLink(crmSyncSource))}
          >
            <StyledButtonContentDiv>
              <Trans id="crm-sync-upsell-button-learn-more">Learn More</Trans>
              <StyledIcon name="externalLinkSmall" />
            </StyledButtonContentDiv>
          </Button>,
        ]}
        limitContentWidth={false}
        shouldIgnoreImagePadding
        helperText={isAdmin ? undefined : <Trans id="crm-sync-upsell-helper-text">{helperText}</Trans>}
      >
        <Trans id="crm-sync-upsell-body">
          Sync your accounts in minutes, and then leverage Salesforce account data in Fronts inbox, rules, and
          analytics.
        </Trans>
      </UpsellCard>
    </UpsellWrapperDiv>
  );
};
